import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IThunkApiExtra, RootState } from "../store";
import { STATUS } from "../globalContext/globalContext.interfaces";
import { IRecoveryConfirmContext, IRecoveryConfirmRequest } from "../globalContext/recovery.interface";
import { recoveryService } from "src/api/recovery/service";

type RecoveryConfirmState = IRecoveryConfirmContext;

// Estado inicial para RecoveryConfirm
export const initialState: RecoveryConfirmState = {
	status: STATUS.init,
	message: '',
	apiStatusCode: null,
};

// Thunk para manejar la confirmación de recuperación de contraseña
export const recoveryConfirm = createAsyncThunk<
  RecoveryConfirmState,
  IRecoveryConfirmRequest,
  { rejectValue: { status: number; message: string } }
>(
	'recovery/recoveryConfirm',
	async (requestData: IRecoveryConfirmRequest, thunkApi) => {
		try {
			if (!requestData) {
				return thunkApi.rejectWithValue({
					status: 400,
					message: 'Request data is missing.',
				});
			}

			const response = await recoveryService.recoveryConfirm(requestData);
			if (response.status === 201) {
				return {
					status: STATUS.idle,
					message: 'Recuperación de contraseña exitosa',
					apiStatusCode: response.status,
				};
			} else {
				return thunkApi.rejectWithValue({
					status: response.status,
					message: response.data.message || 'Error desconocido',
				});
			}
		} catch (error: any) {
			return thunkApi.rejectWithValue({
				status: error.status || 500,
				message: error.message || 'Error en la recuperación de contraseña',
			});
		}
	}
);

// Thunk para validar el token
export const verifyToken = createAsyncThunk<
  { payload: any; status: number },
  string,
  { rejectValue: { status: number; message: string } }
>(
	'recovery/verifyToken',
	async (token: string, thunkApi) => {
		try {
			if (!token) {
				return thunkApi.rejectWithValue({
					status: 400,
					message: 'Token is missing.',
				});
			}

			const response = await recoveryService.verifyToken(token);
			if (response.status === 200) {
				return {
					payload: response.data,
					status: response.data.status,
				};
			} else {
				return thunkApi.rejectWithValue({
					status: response.status,
					message: response.data.message || 'Token validation failed',
				});
			}
		} catch (error: any) {
			return thunkApi.rejectWithValue({
				status: error.status || 500,
				message: error.message || 'Error validating token',
			});
		}
	}
);

// Slice para RecoveryConfirm
export const RecoveryConfirmSlice = createSlice({
	name: 'recoveryConfirm',
	initialState,
	reducers: {},
	extraReducers: builder => {
		builder.addCase(recoveryConfirm.pending, (state) => {
			state.status = STATUS.pending;
			state.apiStatusCode = null;
			state.message = '';
		});
		builder.addCase(recoveryConfirm.fulfilled, (state, action) => {
			state.status = STATUS.idle;
			state.apiStatusCode = action.payload.apiStatusCode;
			state.message = action.payload.message;
		});
		builder.addCase(recoveryConfirm.rejected, (state, action) => {
			state.status = STATUS.error;
			state.apiStatusCode = action.payload?.status || 500;
			state.message = action.payload?.message || 'Error en la recuperación de contraseña';
		});

		//reducers para verifyToken
		builder.addCase(verifyToken.pending, (state) => {
			state.status = STATUS.pending;
			state.apiStatusCode = null;
			state.message = '';
		});
		builder.addCase(verifyToken.fulfilled, (state, action) => {
			state.status = STATUS.idle;
			state.apiStatusCode = action.payload.status;
			state.message = 'Token validado exitosamente';
		});
		builder.addCase(verifyToken.rejected, (state, action) => {
			state.status = STATUS.error;
			state.apiStatusCode = action.payload?.status || 500;
			state.message = action.payload?.message || 'Error al validar el token';
		});
	},
});

// Selectors para obtener el estado y código de estado de la API
export const selectRecoveryStatus = (state: RootState) => state.RecoveryConfirmState.status;
export const selectRecoveryApiStatusCode = (state: RootState) => state.RecoveryConfirmState.apiStatusCode;
export const selectRecoveryMessage = (state: RootState) => state.RecoveryConfirmState.message;

export default RecoveryConfirmSlice.reducer;
